import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  useToast
} from "@chakra-ui/react"
import { gql, useMutation } from "@apollo/client"
import { useAtom } from "jotai"
import React, { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  LOGGED_IN,
  Paths,
  REMEMBER_ME,
  RETURN_URL,
  TOKEN_NAME,
  USER_DETAILS_NAME
} from "../../../common/commonConstants"
import { userDetailsAtom } from "../../Menu/menu.store"
import { getSavedUserDetails } from "../../Menu/menu.support"
import { SnapLogo } from "../../commons/SnapLogo"

const TURNSTILE_SITE_KEY = "0x4AAAAAADJdndOfvqgu2xOB"

const LOGIN = gql`
  mutation LoginUser($username: String!, $password: String!) {
    loginUser(username: $username, password: $password) {
      token
    }
  }
`

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const inputStyle = {
  borderRadius: "8px",
  height: "40px",
  border: "1px solid #ddd",
  _focus: { borderColor: "#861F41", boxShadow: "0 0 0 1px #861F41" }
}

export const LoginModal = ({ isOpen, onClose, onSuccess }: LoginModalProps): JSX.Element => {
  const [loginUser, { data, loading, error }] = useMutation(LOGIN)
  const [, setUserDetails] = useAtom(userDetailsAtom)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem(REMEMBER_ME) === "true")
  const [errorMessage, setErrorMessage] = useState("")
  const [isVerifying, setIsVerifying] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()
  const turnstileContainerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const pendingSubmitRef = useRef<{ username: string; password: string } | null>(null)

  // Invisible Turnstile setup
  useEffect(() => {
    if (!isOpen) return

    const initWidget = (): void => {
      if (turnstileContainerRef.current && (window as any).turnstile && !widgetIdRef.current) {
        widgetIdRef.current = (window as any).turnstile.render(turnstileContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          size: "invisible",
          callback: (_token: string) => {
            setIsVerifying(false)
            if (pendingSubmitRef.current) {
              loginUser({ variables: pendingSubmitRef.current }).catch(() => {})
            }
          },
          "error-callback": () => {
            setIsVerifying(false)
            setErrorMessage("Security check failed. Please try again.")
            widgetIdRef.current = null
          },
          "expired-callback": () => {
            widgetIdRef.current = null
          }
        })
      }
    }

    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')
    if (existingScript && (window as any).turnstile) {
      initWidget()
    } else if (!existingScript) {
      const script = document.createElement("script")
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js"
      script.async = true
      script.defer = true
      script.onload = initWidget
      document.head.appendChild(script)
    } else {
      // Script tag exists but turnstile not ready yet — poll briefly
      const poll = setInterval(() => {
        if ((window as any).turnstile) {
          clearInterval(poll)
          initWidget()
        }
      }, 100)
      setTimeout(() => clearInterval(poll), 5000)
    }

    return () => {
      if (widgetIdRef.current && (window as any).turnstile) {
        try { (window as any).turnstile.remove(widgetIdRef.current) } catch (_) {}
      }
      widgetIdRef.current = null
      pendingSubmitRef.current = null
    }
  }, [isOpen])

  // Handle successful login
  useEffect(() => {
    if (!data) return
    const payloadToken = data.loginUser.token.split(".")[1]
    const decoded = atob(payloadToken)
    localStorage.setItem(LOGGED_IN, "true")
    if (rememberMe) {
      localStorage.setItem(TOKEN_NAME, data.loginUser.token)
      localStorage.setItem(USER_DETAILS_NAME, decoded)
      localStorage.setItem(REMEMBER_ME, "true")
    } else {
      sessionStorage.setItem(TOKEN_NAME, data.loginUser.token)
      sessionStorage.setItem(USER_DETAILS_NAME, decoded)
      localStorage.removeItem(REMEMBER_ME)
    }
    const savedDetails = getSavedUserDetails()
    if (savedDetails) setUserDetails(savedDetails)

    if (onSuccess) {
      onSuccess()
    } else {
      onClose()
      const returnUrl = sessionStorage.getItem(RETURN_URL)
      if (returnUrl) {
        sessionStorage.removeItem(RETURN_URL)
        navigate(returnUrl)
      }
    }
  }, [data])

  useEffect(() => {
    if (!error) return
    const msg = error.graphQLErrors?.[0]?.message || "Invalid email or password."
    setErrorMessage(msg)
    toast({ title: "Login failed", description: msg, status: "error", duration: 5000, isClosable: true, position: "top" })
  }, [error])

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setErrorMessage("")
    pendingSubmitRef.current = { username: email, password }

    if (widgetIdRef.current && (window as any).turnstile) {
      setIsVerifying(true)
      ;(window as any).turnstile.execute(widgetIdRef.current)
    } else {
      // Turnstile not loaded — proceed directly
      loginUser({ variables: { username: email, password } }).catch(() => {})
    }
  }

  const handleClose = (): void => {
    setEmail("")
    setPassword("")
    setErrorMessage("")
    setIsVerifying(false)
    onClose()
  }

  const handleForgotPassword = (): void => {
    handleClose()
    navigate("/resetPassword")
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay bg="rgba(0,0,0,0.35)" backdropFilter="blur(8px)" />
      <ModalContent borderRadius="16px" maxW="420px" overflow="hidden" mx="4">
        {/* Header */}
        <Box bg="#861F41" px="6" py="5" position="relative">
          <Flex align="center" justify="center" flexDir="column" gap="2">
            <SnapLogo props={{ width: "120px", height: "84px" }} />
            <Text color="white" fontSize="12px" letterSpacing="2px" textTransform="uppercase" opacity={0.85}>
              Fire Systems
            </Text>
          </Flex>
          <Button
            position="absolute"
            top="3"
            right="3"
            variant="ghost"
            color="white"
            _hover={{ bg: "rgba(255,255,255,0.15)" }}
            size="sm"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </Button>
        </Box>

        {/* Body */}
        <Box px="6" py="6">
          <Text fontSize="18px" fontWeight="600" color="#861F41" mb="5" textAlign="center">
            Log in to your account
          </Text>
          <form onSubmit={handleSubmit}>
            <Box mb="3">
              <Text fontSize="13px" fontWeight="500" mb="1" color="gray.700">Email address</Text>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                {...inputStyle}
              />
            </Box>

            <Box mb="3">
              <Text fontSize="13px" fontWeight="500" mb="1" color="gray.700">Password</Text>
              <InputGroup>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Your password"
                  required
                  {...inputStyle}
                  pr="10"
                />
                <InputRightElement h="40px" mr="2">
                  <Button
                    size="xs"
                    variant="unstyled"
                    color="gray.500"
                    fontWeight="500"
                    fontSize="12px"
                    minW="auto"
                    h="auto"
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </Box>

            <Flex justify="space-between" align="center" mb="5">
              <Checkbox
                isChecked={rememberMe}
                onChange={() => setRememberMe(v => !v)}
                colorScheme="red"
                size="sm"
              >
                <Text fontSize="13px" color="gray.600">Remember me</Text>
              </Checkbox>
              <Text
                as="span"
                color="#861F41"
                fontSize="13px"
                fontWeight="500"
                cursor="pointer"
                _hover={{ textDecoration: "underline" }}
                onClick={handleForgotPassword}
              >
                Forgot password?
              </Text>
            </Flex>

            {errorMessage && (
              <Text color="red.500" fontSize="13px" mb="3" textAlign="center">{errorMessage}</Text>
            )}

            <Button
              type="submit"
              w="100%"
              bg="#861F41"
              color="white"
              borderRadius="24px"
              height="42px"
              fontSize="15px"
              fontWeight="600"
              isLoading={loading || isVerifying}
              _hover={{ bg: "#6d1934" }}
              _active={{ bg: "#5a1529" }}
            >
              Log In
            </Button>
          </form>

          {/* Invisible Turnstile container */}
          <div ref={turnstileContainerRef} style={{ display: "none" }} />
        </Box>

        {/* Footer */}
        <Box borderTop="1px solid #f0f0f0" px="6" py="4" textAlign="center">
          <Text fontSize="14px" color="gray.600">
            Don't have an account?{" "}
            <Text
              as="span"
              color="#861F41"
              fontSize="14px"
              fontWeight="500"
              cursor="pointer"
              _hover={{ textDecoration: "underline" }}
              onClick={() => { handleClose(); navigate(`${Paths.LOGIN.login}?signup=true`) }}
            >
              Create one here
            </Text>
          </Text>
        </Box>
      </ModalContent>
    </Modal>
  )
}
